/** The client MCP diagnose's runs and their sampled human review (client MCP plan §5.5, §7.3 #4;
 *  backend WPS P4-03): ops' side of the LLM judge.
 *
 *  A client holding `triage.mcp_diagnose` asks its AI "why did this happen?" and our diagnose
 *  answers in prose; an LLM judge reads that prose before release (fail-closed: anything but
 *  `released` withholds it, and the client gets the checks' findings). Every withheld answer and a
 *  sample of the released ones are flagged for a person to review.
 *
 *  - `GET /mcp_diagnose_runs/` (`console_entity_ai`) — every tenant's client runs, newest first,
 *    page-numbered, under the statement-timeout guard; `review=pending` is the review queue. A row
 *    is our facts only: never the client's question or an answer.
 *  - `GET /mcp_diagnose_runs/{task_id}/` — one run: the client's question, the answer the client
 *    got, the AI-written answer the judge withheld, the judge's verdict and note, the review — the
 *    free texts marked untrusted (`content_is_untrusted_data`, `legend`).
 *  - `POST /mcp_diagnose_runs/{task_id}/review/` `{outcome, note}` — records the review (a later
 *    one replaces it); audited in the console as `mcp_diagnose.reviewed`.
 *
 *  Shapes come from the generated spec types; this module adds the query keys and the words. */
import { apiGet, apiPost, ConsoleApiError, extendedListConfig } from './client';
import type { components } from './types';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

type Schemas = components['schemas'];

export type McpDiagnoseRun = Schemas['ConsoleMcpDiagnoseRun'];
export type McpDiagnoseRunDetail = Schemas['ConsoleMcpDiagnoseRunDetail'];
export type McpDiagnoseJudgeOutcome = Schemas['ConsoleMcpDiagnoseJudgeOutcome'];
export type McpDiagnoseJudge = Schemas['ConsoleMcpDiagnoseJudge'];
export type McpDiagnoseReview = Schemas['ConsoleMcpDiagnoseReview'];
export type McpDiagnoseReviewRequest = Schemas['ConsoleMcpDiagnoseReviewRequest'];
export type McpDiagnoseReviewOutcome = McpDiagnoseReviewRequest['outcome'];

/** The longest note a review takes. */
export const REVIEW_NOTE_MAX = 500;

/** The console audit log's event for a review. */
export const REVIEW_AUDIT_EVENT = 'mcp_diagnose.reviewed';

// ---- list ------------------------------------------------------------------

/** The `review` filter: the queue (`pending`), done, either, never flagged. */
export type ReviewFilter = 'pending' | 'reviewed' | 'flagged' | 'none';

/** The grid's filters, by their API names (the page's `useGridFilters` defs resolve to these). */
export interface McpDiagnoseRunFilters {
  /** One id or a comma list of ids. */
  tour_operator_id?: TenantIdsParam;
  review?: ReviewFilter;
  /** The flag's reason, one or a comma-separated list. */
  reason?: string;
  /** The judge's outcome (or `none`: not judged), one or a comma-separated list. */
  judge?: string;
  /** The review's outcome, one or a comma-separated list. */
  outcome?: string;
  created_after?: string;
  created_before?: string;
}

export interface McpDiagnoseRunListParams extends McpDiagnoseRunFilters {
  ordering?: string;
  timeout?: 'extended';
  page?: number;
  page_size?: number;
}

type QueryValue = string | number | boolean | null | undefined;

function toQuery(params: Record<string, QueryValue>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

const path = (taskId: string) => `/mcp_diagnose_runs/${encodeURIComponent(taskId)}/`;

export function list(
  params: McpDiagnoseRunListParams = {},
): Promise<PaginatedResponse<McpDiagnoseRun>> {
  return apiGet(`/mcp_diagnose_runs/${toQuery({ ...params })}`, extendedListConfig(params.timeout));
}

export function retrieve(taskId: string): Promise<McpDiagnoseRunDetail> {
  return apiGet(path(taskId));
}

/** Records the review and answers the run. */
export function recordReview(
  taskId: string,
  body: McpDiagnoseReviewRequest,
): Promise<McpDiagnoseRunDetail> {
  return apiPost(`${path(taskId)}review/`, body);
}

/** A readable message from a refused review: DRF's field-keyed `{outcome|note: ["…"]}`, or the
 *  body's `detail`. */
export function reviewErrorText(e: unknown, fallback = 'The review was not recorded.'): string {
  if (!(e instanceof ConsoleApiError)) return e instanceof Error ? e.message : fallback;
  const body = e.body as Record<string, unknown> | undefined;
  for (const field of ['outcome', 'note']) {
    const v = body?.[field];
    if (typeof v === 'string' && v) return v;
    if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  }
  if (typeof body?.detail === 'string' && body.detail) return body.detail;
  if (e.status === 403) return 'Reviewing needs the console_entity_ai permission.';
  return e.message || fallback;
}

/** Everything under `mcpDiagnoseRunKeys.all`, so a review refreshes the queue and the run. */
export const mcpDiagnoseRunKeys = {
  all: ['mcpDiagnoseRuns'] as const,
  list: ['mcpDiagnoseRuns', 'list'] as const,
  detail: (taskId: string) => ['mcpDiagnoseRuns', 'detail', taskId] as const,
};

// ---- words -------------------------------------------------------------------

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';

interface Word<V extends string> {
  value: V;
  label: string;
  color: ChipColor;
}

export const REVIEW_FILTERS: readonly Word<ReviewFilter>[] = [
  { value: 'pending', label: 'Waiting for review', color: 'warning' },
  { value: 'reviewed', label: 'Reviewed', color: 'success' },
  { value: 'flagged', label: 'Flagged (either)', color: 'default' },
  { value: 'none', label: 'Never flagged', color: 'default' },
];

/** Why a run is flagged for review. */
export const REVIEW_REASONS: readonly Word<McpDiagnoseReview['reason']>[] = [
  { value: 'withheld', label: 'Answer withheld', color: 'error' },
  { value: 'sampled', label: 'Sampled', color: 'info' },
  { value: 'manual', label: 'Reviewed by hand', color: 'default' },
];

/** The judge's verdict; `none` is the filter's "no AI-written answer to judge". */
export const JUDGE_OUTCOMES: readonly Word<McpDiagnoseJudgeOutcome | 'none'>[] = [
  { value: 'released', label: 'Released', color: 'success' },
  { value: 'flagged', label: 'Flagged', color: 'error' },
  { value: 'scanner', label: 'Output scanner', color: 'error' },
  { value: 'unusable', label: 'Unusable verdict', color: 'warning' },
  { value: 'refused', label: 'Judge declined', color: 'warning' },
  { value: 'failed', label: 'Judge failed', color: 'warning' },
  { value: 'none', label: 'Not judged', color: 'default' },
];

/** What a person found. */
export const REVIEW_OUTCOMES: readonly Word<McpDiagnoseReviewOutcome>[] = [
  { value: 'ok', label: 'OK', color: 'success' },
  { value: 'problem', label: 'Problem', color: 'error' },
];

export const RUN_STATES: readonly Word<McpDiagnoseRun['state']>[] = [
  { value: 'pending', label: 'Queued', color: 'default' },
  { value: 'running', label: 'Running', color: 'info' },
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'failed', label: 'Failed', color: 'error' },
  { value: 'cancelled', label: 'Cancelled', color: 'default' },
];

export const SUBJECT_SCOPE_LABELS: Record<McpDiagnoseRun['subject']['scope'], string> = {
  booking: 'Booking',
  order: 'Order',
  guest: 'Guest',
};

/** Why the client's answer is partial (the diagnose's `partial_reason`). */
export const PARTIAL_REASON_LABELS: Record<string, string> = {
  run_budget: 'Run budget used up',
  daily_budget: 'Daily budget used up',
  tool_limit: 'Evidence limit reached',
  declined: 'Declined the question',
  no_answer: 'No answer',
  off_topic: 'Not about these bookings',
  withheld: 'Answer withheld by the judge',
};

/** What the evidence says about the question (the diagnose's `conclusion`). */
export const CONCLUSION_LABELS: Record<string, string> = {
  explained: 'Explained',
  partly_explained: 'Partly explained',
  not_explained: 'Not explained by the evidence',
};

const wordOf = <V extends string>(words: readonly Word<V>[], value: string) =>
  words.find((w) => w.value === value);

export const reviewReasonLabel = (v: string) => wordOf(REVIEW_REASONS, v)?.label ?? v;
export const reviewReasonColor = (v: string): ChipColor =>
  wordOf(REVIEW_REASONS, v)?.color ?? 'default';
export const judgeLabel = (v: string) => wordOf(JUDGE_OUTCOMES, v)?.label ?? v;
export const judgeColor = (v: string): ChipColor => wordOf(JUDGE_OUTCOMES, v)?.color ?? 'default';
export const reviewOutcomeLabel = (v: string) => wordOf(REVIEW_OUTCOMES, v)?.label ?? v;
export const reviewOutcomeColor = (v: string): ChipColor =>
  wordOf(REVIEW_OUTCOMES, v)?.color ?? 'default';
export const runStateLabel = (v: string) => wordOf(RUN_STATES, v)?.label ?? v;
export const runStateColor = (v: string): ChipColor => wordOf(RUN_STATES, v)?.color ?? 'default';
export const partialReasonLabel = (v: string) => PARTIAL_REASON_LABELS[v] ?? v;
export const conclusionLabel = (v: string) => CONCLUSION_LABELS[v] ?? v;

/** "Complete", "Partial · Answer withheld by the judge", or "—" while the run has no answer. */
export function answerText(run: Pick<McpDiagnoseRun, 'status' | 'partial_reason'>): string {
  if (run.status === 'complete') return 'Complete';
  if (run.status === 'partial') {
    return run.partial_reason ? `Partial · ${partialReasonLabel(run.partial_reason)}` : 'Partial';
  }
  return '—';
}

/** "Waiting for review" / "Reviewed · Problem" / "Not flagged" — a run's review in one phrase. */
export function reviewPhrase(review: McpDiagnoseRun['review']): string {
  if (!review) return 'Not flagged';
  if (review.state === 'pending') return 'Waiting for review';
  return review.outcome ? `Reviewed · ${reviewOutcomeLabel(review.outcome)}` : 'Reviewed';
}
